import { getDoc, doc } from 'firebase/firestore'
import { getProjectCollection } from '../../../services/firebase/firebaseConfig'
import { OrderProduct } from '../../../services/firebase/interface/OrderProduct'
import { normalizeOrder } from './normalizeOrder'
import type { NormalizedOrder } from './normalizeOrder'

/** The three globalUnits ({ dataSourceUnit, selected }) fetchProductData needs. */
export interface DisplayUnits {
  weight?: any
  volume?: any
  length?: any
}

/**
 * Enrich an editor row's ProductJson from TMS_INVENTORY.
 *
 * Dispatch/optimizer orders (created by functions/utils/CreateOrdersFromStops) store a
 * THIN ProductJson line — `{ InventoryId, IsManagedByAsset, ProductUpdateTrigger }` —
 * with the real Quantity/Weight/Volume/Sku/Name living on the referenced TMS_INVENTORY
 * doc. OrderTable enriches every order this way on the grid (pages/OrderTable/index.tsx
 * ~1219). The dispatch open-details path did NOT, so the order editor showed
 * `Quantity || 1` → 1, weight 0, "Unknown". This mirrors OrderTable's enrichment so the
 * opened order carries real per-item data (and recomputed, unit-converted totals).
 */
export async function enrichEditorRowProducts(
  row: Record<string, any>,
  units: DisplayUnits = {},
): Promise<Record<string, any>> {
  const pj = Array.isArray(row?.ProductJson) ? row.ProductJson : []
  if (pj.length === 0) return row

  const w = units.weight ?? {}
  const v = units.volume ?? {}
  const l = units.length ?? {}

  let totalWeight = 0
  let totalVolume = 0
  const products = await Promise.all(
    pj.map(async (line: any) => {
      const op = new OrderProduct()
      try {
        await op.fetchProductData(line, row.id, w, v, l)
      } catch (e) {
        /* fall through — keep the raw line's data below */
      }
      // fetchProductData early-returns (no InventoryId) or throws for archived/mismatched
      // lines, leaving `op` at its blank defaults. Detect that and keep the raw line.
      const enrichedOk = !!(op.InventoryId || op.AssetId || op.Name || op.Sku)
      const product = enrichedOk
        ? { ...op, InventoryId: op.InventoryId || line?.InventoryId || null }
        : { ...line }
      const qty = Number((product as any).Quantity) || 1
      totalWeight += (Number((product as any).Weight) || 0) * qty
      totalVolume += (Number((product as any).Volume) || 0) * qty
      return product
    }),
  )

  return { ...row, ProductJson: products, TotalWeight: totalWeight, TotalVolume: totalVolume }
}

/**
 * Adapt a NormalizedOrder into the flat row shape TMS_PORTAL's order editor
 * consumes via GlobalContext.table.selectedTableOrder. OrderTable builds this
 * same shape from a Firestore doc: the doc's `fields` are spread to the top
 * level, plus `id` and a few derived meta keys.
 */
export function toEditorRow(order: NormalizedOrder): Record<string, any> {
  const raw = order.raw ?? {}
  const fields = raw.fields ?? raw ?? {}
  return {
    ...fields,
    id: order.id,
    ImportedAt: raw.importedAt ?? null,
    UpdatedAt: raw.lastUpdated ?? null,
    LastArchived: raw.lastArchived ?? null,
    geminiResult: raw.geminiResult ?? null,
  }
}

/**
 * Fetch a single TMS_ORDERS doc by id and produce the flat editor row the portal's
 * OrderDetailsTabs consumes (via GlobalContext.table.selectedTableOrder). Used to open
 * the full order editor from an optimizer stop, which only carries an id.
 */
export async function fetchEditorRow(stopId: string, units: DisplayUnits = {}): Promise<Record<string, any>> {
  // Read from the same project-scoped TMS_ORDERS collection the dashboard "Open
  // details" (Today mode) uses, so the editor receives the full order document.
  const snap = await getDoc(doc(getProjectCollection('TMS_ORDERS'), stopId))
  const normalized = normalizeOrder({ id: (snap as any).id ?? stopId, data: () => (snap as any).data?.() ?? {} })
  return enrichEditorRowProducts(toEditorRow(normalized), units)
}
